/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useCallback, useContext, useEffect, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from 'use-debounce';

import SummaryViewerReportModal from '../../modals/SummaryViewerReportModal';
import MeetingDetailsModal from '../../modals/MeetingDetailsModal';
import MoveModal from '../../modals/MoveModal';
import Th from '../Th';
import Row from './Row';
import CreateMeetingMobileButton from '../CreateMeetingMobileButton';
import EmptyFolderMessage from '../Library/EmptyFolderMessage';
import Pagination from '../Pagination';
import RequestReviewModal from '../../modals/RequestReviewModal';
import PermissionManager from '../PermissionManager';
import { RESOURCE_TYPES } from '../../services/permissions/constants';

import { UiContext } from '../../context/UiContext';
import useCheckMobileScreen from '../../hooks/useCheckMobileScreen';
import MeetingService from '../../services/MeetingService';
import UserService from '../../services/UserService';
import copyFolderLink from '../../helpers/copyFolderLink';
import useMeetingProcessing from '../../hooks/useMeetingProcessing';
import useMeetingReady from '../../hooks/useMeetingReady';
import usePublishFailed from '../../hooks/usePublishFailed';
import { MEETING_TABS, ORG_ROLES, USER_ROLES } from '../../constants/main';

const rowHeight = 70;
const restHeight = 112 + 108 + 44 + 64;
const mobileItemsRange = Math.floor(
  (window.innerHeight - restHeight) / rowHeight
);

const ths = [
  {
    title: 'Name',
    field: 'title',
  },
  {
    title: 'Owner',
    field: 'ownerName',
  },
  {
    title: 'Date Uploaded',
    field: 'createdAt',
  },
  {
    title: 'Video',
    field: 'duration',
  },
  {
    title: 'Version',
    field: 'latestPublishedVersion',
    noSort: true,
  },
  {
    title: 'Action',
    type: 'action',
    noSort: true,
  },
];

export default function MeetingsList({
  classes,
  searchInput,
  setSearchInput,
  tab,
  destinationMeetingId,
  prevSectionId,
  deleted,
}) {
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [page, setPage] = useState(1);
  const [debouncedSearchInput] = useDebounce(searchInput, 500);
  const [isMeetingDetailsModalVisible, setIsMeetingDetailsModal] =
    useState(false);
  const [sortField, setSortField] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [isSummaryModalVisible, setIsSummaryModalVisible] = useState(false);
  const [currentContentTitle, setCurrentContentTitle] = useState(null);
  const [currentMeetingId, setCurrentMeetingId] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isRequestReviewModalVisible, setIsRequestReviewModalVisible] =
    useState(false);
  const [requestReviewMeetingId, setRequestReviewMeetingId] = useState(null);
  const [isPermissionManagerVisible, setIsPermissionManagerVisible] =
    useState(false);
  const [permissionManagerResource, setPermissionManagerResource] =
    useState(null);
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [moveMeetingId, setMoveMeetingId] = useState(null);
  const [moveMeetingName, setMoveMeetingName] = useState(null);
  const [moveCurrentFolderId, setMoveCurrentFolderId] = useState(null);

  const navigate = useNavigate();
  const isMobile = useCheckMobileScreen();
  const processing = useMeetingProcessing();

  const queryClient = useQueryClient();

  const { showConfirmActionModal, notifySuccess, notifyError } = useContext(UiContext);

  const itemsRange = isMobile ? mobileItemsRange : 10;

  const { data: user } = useQuery('me', UserService.getMe);
  const canMoveOnMyVideos =
    user?.orgRole?.access === ORG_ROLES.OWNER ||
    user?.role === USER_ROLES.SUPER_ADMIN;

  const { data, refetch } = useQuery(
    [
      'meetings',
      page,
      debouncedSearchInput,
      itemsRange,
      tab,
      sortField,
      sortOrder,
      deleted,
    ],
    () =>
      MeetingService.getMeetings({
        page,
        search: debouncedSearchInput,
        limit: itemsRange,
        type: tab,
        sortField,
        sortOrder,
        deleted,
      }),
    {
      keepPreviousData: true,
      refetchInterval: isProcessing ? 10000 : false,
      onSuccess: (dt) => {
        const meetingsArray = dt?.rows || dt;
        if (meetingsArray?.some((m) => m.status === 'in progress')) {
          setIsProcessing(true);
        } else {
          setIsProcessing(false);
        }
      },
    }
  );

  const meetings = data?.rows || data;

  const openRequestReview = useCallback((meetingId) => {
    setRequestReviewMeetingId(meetingId);
    setIsRequestReviewModalVisible(true);
  }, []);

  const closeRequestReview = useCallback(() => {
    setIsRequestReviewModalVisible(false);
    setRequestReviewMeetingId(null);
  }, []);

  // TODO: REMOVE
  // Refetch meeting on processing end and notify user
  useEffect(() => {
    if (processing.step === 'finishing') {
      refetch();

      const processedMeeting = meetings.find(
        (meeting) => meeting.id === processing.meetingId
      );
      const isSavedSectionMeeting = processedMeeting?.type === 'saved';

      if (!isSavedSectionMeeting && processedMeeting) {
        // Check if meeting has video content or image-only content
        const hasVideo = processedMeeting.s3FileKey && processedMeeting.duration > 0;
        const message = hasVideo 
          ? 'Your video is ready to share.'
          : 'Your content is ready to share.';

        showConfirmActionModal({
          message,
          title: '... and done!',
          noButtons: true,
          timeToClose: 3000,
        });
      }
    }
  }, [meetings, processing, refetch, showConfirmActionModal, tab]);

  // Refetch meeting on processing end and notify user
  useMeetingReady((meetingId) => {
    refetch();

    const processedMeeting = meetings.find(
      (meeting) => meeting.id === meetingId
    );
    const isSavedSectionMeeting = processedMeeting?.type === 'saved';

    if (!isSavedSectionMeeting && processedMeeting) {
      // Check if meeting has video content or image-only content
      const hasVideo = processedMeeting.s3FileKey && processedMeeting.duration > 0;
      const message = hasVideo 
        ? 'Your video is ready to share.'
        : 'Your content is ready to share.';

      showConfirmActionModal({
        message,
        title: '... and done!',
        noButtons: true,
      });
    }
  });

  usePublishFailed(() => {
    refetch();
    notifyError('Publishing failed. Your video has been reverted to draft.');
  });

  const { mutate: toggleMeetingTrashStatusMutation } = useMutation(
    MeetingService.toggleMeetingTrashStatus,
    {
      onSuccess: () => {
        refetch();
      },
    }
  );

  const { mutate: updateMeetingMutation } = useMutation(
    MeetingService.updateMeeting,
    {
      onSuccess: () => {
        refetch();
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update video';
        notifyError(errorMessage);
      },
    }
  );

  const { mutate: importMeetingMutation } = useMutation(
    MeetingService.importMeeting,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['sections', destinationMeetingId], {
          exact: true,
        });

        queryClient.refetchQueries(['sections', destinationMeetingId], {
          exact: true,
          active: false,
        });
        navigate(`/my-videos/${destinationMeetingId}/edit`);
      },
    }
  );

  const { mutateAsync: moveMeetingMutation } = useMutation(
    ({ meetingId: id, destinationFolderId }) =>
      MeetingService.moveMeeting(id, destinationFolderId),
    {
      onSuccess: () => {
        refetch();
      },
      onError: (error) => {
        notifyError(
          error?.response?.data?.message || 'Failed to move video'
        );
      },
    }
  );

  const handleMoveMeeting = useCallback((meeting) => {
    setMoveMeetingId(meeting.id);
    setMoveMeetingName(meeting.title);
    setMoveCurrentFolderId(meeting.folderId || null);
    setIsMoveModalOpen(true);
  }, []);

  const handleMoveConfirm = useCallback(
    async (destinationFolderId) => {
      // My Videos Move never allows root (null destination)
      if (!destinationFolderId || !moveMeetingId) {
        return;
      }

      await moveMeetingMutation({
        meetingId: moveMeetingId,
        destinationFolderId,
      });
      notifySuccess('Meeting moved successfully.');
      setIsMoveModalOpen(false);
      setMoveMeetingId(null);
      setMoveMeetingName(null);
      setMoveCurrentFolderId(null);
    },
    [moveMeetingId, moveMeetingMutation, notifySuccess]
  );

  const handleMoveCancel = useCallback(() => {
    setIsMoveModalOpen(false);
    setMoveMeetingId(null);
    setMoveMeetingName(null);
    setMoveCurrentFolderId(null);
  }, []);

  const switchPage = (pageNumber) => {
    setPage(pageNumber);
  };

  const handleDetails = useCallback((meeting) => {
    setSelectedMeeting(meeting);
    setIsMeetingDetailsModal(true);
  }, []);

  const handleDelete = useCallback(
    (meetingId) => {
      showConfirmActionModal({
        message: 'Are you sure you want to delete this video?',
        onConfirm: () => toggleMeetingTrashStatusMutation(meetingId),
      });
    },
    [showConfirmActionModal, toggleMeetingTrashStatusMutation]
  );

  const handleCopyFolderLink = useCallback(
    async (folderId, meetingId) => {
      try {
        if (folderId) {
          copyFolderLink(folderId, meetingId);
        } else {
          const { link } = await MeetingService.shareMeetingLink({
            meetingId,
            security: 'public',
          });

          navigator.clipboard.writeText(link);
        }

        notifySuccess(`Link copied to clipboard`);
      } catch (error) {
        console.error(error);
      }
    },
    [notifySuccess]
  );

  const handleUpdateMeeting = useCallback(
    (meetingId, title) => {
      updateMeetingMutation({
        meetingId,
        title,
      });
    },
    [updateMeetingMutation]
  );

  const handleMeetingClick = useCallback(
    (meetingId) => {
      if (!destinationMeetingId) {
        return;
      }

      importMeetingMutation({
        sourceMeetingId: meetingId,
        destinationMeetingId,
        prevSectionId,
      });
    },
    [destinationMeetingId, importMeetingMutation, prevSectionId]
  );

  const handlePlay = useCallback(
    (meetingId) => {
      navigate(`/my-videos/${meetingId}`);
    },
    [navigate]
  );

  useEffect(() => {
    setPage(1);
    refetch();
  }, [debouncedSearchInput, refetch]);

  useEffect(() => {
    if (data?.rows) {
      if (data.rows.length === 0 && page !== 1) {
        setPage((prevPage) => prevPage - 1);
      }
    }
  }, [data?.rows, page]);

  // eslint-disable-next-line no-unused-vars
  const handleManageMeetingAccess = useCallback((meeting) => {
    setPermissionManagerResource(meeting);
    setIsPermissionManagerVisible(true);
  }, []);

  return (
    <>
      <div className={classes.tableContainer}>
        <table className={classes.table}>
          <thead>
            <tr>
              {ths.map((th) => (
                <Th
                  key={th.field}
                  classes={classes}
                  sortField={sortField}
                  sortOrder={sortOrder}
                  setSortField={setSortField}
                  setSortOrder={setSortOrder}
                  field={th.field}
                  title={th.title}
                  searchInput={searchInput}
                  setSearchInput={setSearchInput}
                  noSort={th.noSort}
                />
              ))}
            </tr>
          </thead>
          {meetings?.length ? (
            <tbody>
              {meetings.map((meeting) => {
                return (
                  <Row
                    refetch={refetch}
                    key={meeting.id}
                    classes={classes}
                    meeting={meeting}
                    isMobile={isMobile}
                    tab={tab}
                    user={user}
                    handleUpdateMeeting={handleUpdateMeeting}
                    handlePlay={handlePlay}
                    handleCopyFolderLink={handleCopyFolderLink}
                    handleMeetingClick={handleMeetingClick}
                    destinationMeetingId={destinationMeetingId}
                    handleDetails={handleDetails}
                    handleDelete={handleDelete}
                    setCurrentContentTitle={setCurrentContentTitle}
                    setCurrentMeetingId={setCurrentMeetingId}
                    setIsSummaryModalVisible={setIsSummaryModalVisible}
                    handleRequestReview={openRequestReview}
                    handleManageAccess={handleManageMeetingAccess}
                    handleMove={
                      tab === MEETING_TABS.MY_VIDEOS && canMoveOnMyVideos
                        ? handleMoveMeeting
                        : undefined
                    }
                  />
                );
              })}
            </tbody>
          ) : (
            <p className={classes.noMeetings}>
              {debouncedSearchInput.length ? (
                "Oops! We can't find any videos with that name."
              ) : (
                <EmptyFolderMessage />
              )}
            </p>
          )}
        </table>
      </div>

      {+data?.count > itemsRange && (
        <Pagination
          activePage={page}
          itemsCountPerPage={itemsRange}
          totalItemsCount={+data?.count || 0}
          pageRangeDisplayed={itemsRange}
          onChange={switchPage}
        />
      )}

      <CreateMeetingMobileButton />

      <MeetingDetailsModal
        meeting={selectedMeeting}
        show={isMeetingDetailsModalVisible}
        handleClose={() => setIsMeetingDetailsModal(false)}
      />
      <SummaryViewerReportModal
        show={isSummaryModalVisible}
        handleClose={() => setIsSummaryModalVisible(false)}
        contentTitle={currentContentTitle}
        contentType="meeting"
        id={currentMeetingId}
      />
      <RequestReviewModal
        show={isRequestReviewModalVisible}
        handleClose={closeRequestReview}
        meetingId={requestReviewMeetingId}
      />
      {isPermissionManagerVisible && permissionManagerResource && (
        <PermissionManager
          show={isPermissionManagerVisible}
          onClose={() => {
            setIsPermissionManagerVisible(false);
            setPermissionManagerResource(null);
          }}
          resourceId={permissionManagerResource.id}
          resourceType={RESOURCE_TYPES.MEETING}
          resourceName={permissionManagerResource.title}
          user={user}
          onResourceUpdate={() => {
            refetch();
          }}
        />
      )}
      <MoveModal
        isOpen={isMoveModalOpen}
        onClose={handleMoveCancel}
        itemType="meeting"
        itemId={moveMeetingId}
        itemName={moveMeetingName}
        currentFolderId={moveCurrentFolderId}
        allowedFolderIds={null}
        onMove={handleMoveConfirm}
        isOrgOwner={canMoveOnMyVideos}
        allowRoot={false}
      />
    </>
  );
}
